import { Component, HostBinding, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { UserInfoModel } from '@core/models/models';
import { UserDashboardService } from '@core/services/apis/user-dashboard.service';
import { DpadZoneType, Mode } from '@core/constants';
import { DeviceType } from '@core/constants/enum.common';
import { LOCAL_STORAGE_KEY } from '@core/configs';
import { USER_IMGS } from '@core/constants/text.common';
import { AvatarDealerService, AvatarStatus, DetectDeviceService, LocalStorageService } from '@core/services/common';
import { Subscription } from 'rxjs';

@Component({
  selector: 'caremoni-user-info',
  templateUrl: './user-info.component.html',
  styleUrl: './user-info.component.scss'
})
export class UserInfoComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: UserInfoModel = new UserInfoModel();
  @Input() config: any = {};

  @Input() dpadX:number = -1;
  @Input() dpadY:number = -1;
  DpadZoneType = DpadZoneType;

  fullName: string = "";
  labelCssStyle: any = {};
  isShortTerm: boolean = false;
  isBathScreen: boolean = false;

  /** TV and mobile/tablet layouts render the avatar in a tight cell where the
   *  5px inset breaks the layout, so the padding is dropped via this host class. */
  @HostBinding('class.compact-avatar') compactAvatar = false;

  private avatarSubscription?: Subscription;
  private avatarStatusSubscription?: Subscription;
  /** Drives the loading spinner: 'loading' → default + spinner; terminal → no spinner. */
  avatarStatus: AvatarStatus = 'idle';

  constructor(
    private userDashboardService: UserDashboardService,
    private avatarDealerService: AvatarDealerService,
    private detectDeviceService: DetectDeviceService,
    private localStorageService: LocalStorageService
  ){}

  ngOnInit(): void {
    if (!this.config) this.config = {};
    this.compactAvatar = this.isTvLayout() || this.isMobileLayout();
    // Get isShortTerm from config
    this.isShortTerm = this.config.isShortTerm ?? false;
    this.isBathScreen = this.config?.isBathScheduleScreen ?? false;
    const isFullscreen: boolean = !!document.fullscreenElement;
    this.labelCssStyle = { 'font-size': isFullscreen ? '40cqmin' : '48cqmin' }
    this.config.cssStyle = { width: '100%', height: '100%', color: '#000000' };
    if (this.data) {
      this.fullName = (this.data?.family || '') + (this.data?.given || '');
      // set width, height default
      if (this.config?.cssStyle) this.config.cssStyle = this.config?.cssStyle;
      if(!this.data.face_img)
        this.data.face_img = this.data.sex == 1 ? USER_IMGS.MEN : USER_IMGS.WOMEN;
      this.bindAvatar();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.fullName = (this.data?.family || '') + (this.data?.given || '');
      if (!this.data.face_img) this.data.face_img = this.data.sex == 1 ? USER_IMGS.MEN : USER_IMGS.WOMEN;
      this.bindAvatar();
    }
  }

  ngOnDestroy(): void {
    this.avatarSubscription?.unsubscribe();
    this.avatarStatusSubscription?.unsubscribe();
    this.userDashboardService.triggerUserUpdate(undefined);
  }

  /** TV mode is the app-wide signal persisted at TV login (same check as check-sheet-base). */
  private isTvLayout(): boolean {
    return this.localStorageService.getItem(LOCAL_STORAGE_KEY.mode) == Mode.TV;
  }

  private isMobileLayout(): boolean {
    const deviceType = this.detectDeviceService.getDeviceType();
    return deviceType === DeviceType.MOBILE || deviceType === DeviceType.TABLET;
  }

  upperCaseText(text: string | undefined): any {
    return (text ?? "").toUpperCase();
  }

  sendUserData() {
    this.userDashboardService.triggerUserUpdate(this.data.daicd);
  }

  /** The avatar image failed to load (e.g. its S3 object is gone): show the
   *  default and ask the dealer to invalidate + re-fetch once. */
  onAvatarError(): void {
    const def = this.data?.sex == 1 ? USER_IMGS.MEN : USER_IMGS.WOMEN;
    if (this.data.face_img === def) return; // default itself failed — stop
    this.data.face_img = def;
    if (this.data?.daicd) this.avatarDealerService.retryFailedUrl(this.data.daicd);
  }

  private bindAvatar(): void {
    this.avatarSubscription?.unsubscribe();
    this.avatarStatusSubscription?.unsubscribe();
    if (!this.data?.daicd) return;

    const cachedAvatarUrl = this.avatarDealerService.getAvatarUrl(this.data.daicd);
    if (cachedAvatarUrl) {
      this.data.face_img = cachedAvatarUrl;
    }

    this.avatarSubscription = this.avatarDealerService.avatarUrl$(this.data.daicd).subscribe(avatarUrl => {
      if (avatarUrl) {
        this.data.face_img = avatarUrl;
      }
    });

    this.avatarStatusSubscription = this.avatarDealerService.status$(this.data.daicd).subscribe(status => {
      this.avatarStatus = status;
    });
  }

}
